import { Intervention } from "../details/readOnlyComponents/ReadOnlyInterventionsContainer";
import { DisplayValue } from "../details/tabs/types";
import type { InternalDetails } from "../details/readOnlyComponents/ReadOnlyInternalDetailsContainer";
import type { ProgrammeInfo } from "../details/readOnlyComponents/ReadOnlyProgrammeInfoContainer";
import {
  OutcomesByVersion,
  DetailedSolutionApiResponse,
  SolutionPayload,
  Intervention as PayloadIntervention,
  Coordinates,
} from "../types";

import { countryFormatter } from "../../../shared/utils";
import { placeholder } from "./defaults";
import { getDisplayValue, isMissingDisplayValue } from "./string-manipulation";
import { getArrayDisplayValues } from "./array-manipulation";

export const normaliseOutcomes = (payload: SolutionPayload) =>
  payload.outcomes?.reduce<
    Array<{
      unit: string;
      value: number;
      isUnitOfSale: boolean;
      startDate?: number;
      endDate?: number;
    }>
  >((outcomes, outcome) => {
    if (!outcome.unit || !outcome.value) return outcomes;

    const hasStartDate =
      outcome.startDate !== null && outcome.startDate !== undefined;
    const hasEndDate =
      outcome.endDate !== null && outcome.endDate !== undefined;

    return [
      ...outcomes,
      {
        unit: outcome.unit,
        value:
          typeof outcome.value === "number"
            ? outcome.value
            : Number.parseFloat(outcome.value),
        isUnitOfSale: outcome.isUnitOfSale,
        ...(hasStartDate ? { startDate: Number(outcome.startDate) } : {}),
        ...(hasEndDate ? { endDate: Number(outcome.endDate) } : {}),
      },
    ];
  }, []);

export function getReadOnlyInterventions(
  interventions?: DetailedSolutionApiResponse["interventions"],
): Array<Intervention> {
  return (
    interventions?.map((intervention) => ({
      action:
        intervention.action?.filter((value): value is string =>
          Boolean(value),
        ) ?? [],
      ecosystem:
        intervention.ecosystem?.filter((value): value is string =>
          Boolean(value),
        ) ?? [],
    })) ?? []
  );
}

export const normaliseInterventions = (payload: SolutionPayload) =>
  payload.interventions?.reduce<Array<PayloadIntervention>>(
    (interventions, intervention) => {
      const hasAction = intervention.action?.some(
        (action) => action?.trim() !== "",
      );
      const hasEcosystem = intervention.ecosystem?.some(
        (ecosystem) => ecosystem?.trim() !== "",
      );

      if (!hasAction && !hasEcosystem) return interventions;

      return [
        ...interventions,
        { action: intervention.action, ecosystem: intervention.ecosystem },
      ];
    },
    [],
  );

export function getReadOnlyProgrammeInfo(
  programmeInfo?: DetailedSolutionApiResponse["programmeInfo"],
): ProgrammeInfo {
  return {
    carbonCreditProgramme: programmeInfo?.carbonCreditProgramme ?? [],
    accountingMethodology: programmeInfo?.accountingMethodology ?? "",
    projectDescriptionDocument: programmeInfo?.projectDescriptionDocument ?? "",
    assetType: programmeInfo?.assetType,
  };
}

export function getReadOnlyInternalDetails(
  internalUseOnly: DetailedSolutionApiResponse["internalUseOnly"],
): InternalDetails {
  return {
    websiteUrl: internalUseOnly?.websiteUrl ?? "",
    xeroAccountNumber: internalUseOnly?.xeroAccountNumber ?? "",
    researchTeamNotes: internalUseOnly?.researchTeamNotes ?? "",
    additionalResourceLink: internalUseOnly?.additionalResourceLink ?? "",
    stripeProductUrl: internalUseOnly?.stripeProductUrl ?? "",
  };
}

export function getCountryNameValues(values?: string | Array<string>): string {
  const countryCodes = typeof values === "string" ? [values] : values;
  const countryNames = countryCodes?.map(
    (countryCode) => countryFormatter(countryCode)?.name,
  );

  return getArrayDisplayValues(countryNames);
}

export function getAssessmentMethodologyDisplayValue(
  value?: DisplayValue,
): string {
  if (isMissingDisplayValue(value)) return placeholder;
  return value === "KEYSTONE_2.0" ? "Keystone 2" : "Keystone 3";
}

export function getProjectUrlValue(value?: DisplayValue): string {
  if (isMissingDisplayValue(value)) return placeholder;
  return `https://earthly.org/${value}`;
}

export function getFormattedDevelopmentStage(
  developmentStage?: Array<string>,
): string {
  return getArrayDisplayValues(developmentStage, "singleLine");
}

export function getUnitOfSale(
  outcomes?: OutcomesByVersion,
): string | undefined {
  return outcomes?.v3.find((outcome) => outcome.isUnitOfSale)?.unit;
}

export function isScoreAvailable(score: unknown): score is number {
  return typeof score === "number" && Number.isFinite(score);
}

export function getScoreDisplayValue(score: unknown): string {
  return getDisplayValue(
    isScoreAvailable(score) ? score.toFixed(1) : undefined,
  );
}

export function getCoordinatesDisplayValue(
  coordinates?: Partial<Coordinates>,
): string {
  const latitude = coordinates?.latitude;
  const longitude = coordinates?.longitude;

  if (
    typeof latitude !== "number" ||
    !Number.isFinite(latitude) ||
    typeof longitude !== "number" ||
    !Number.isFinite(longitude)
  ) {
    return placeholder;
  }

  return `Lat: ${latitude}, Lon: ${longitude}`;
}
